import React, { useEffect, useRef, useState } from "react";
import api from "../../api/api";
import { responseError } from "../../api/apiError";
import { ErrorMessages } from "../../constants/messages";
import { fileName } from "../utils";

type Role = "user" | "assistant";

interface Message {
    id: string;
    role: Role;
    text: string;
    timestamp: number;
}

interface AiAgentProps {
    authToken?: string;
    identityProvider?: string;
}

const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    boxSizing: "border-box",
    background: "#f4f4f4",
    position: "relative", // added so the clear button can float (absolute)
};

const listStyle: React.CSSProperties = {
    height: `calc(100vh - 265px)`,
    overflowY: "auto",
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginTop: "8px",
    marginBottom: "8px",
};

const bubbleBase: React.CSSProperties = {
    maxWidth: "78%",
    padding: "10px 12px",
    borderRadius: 10,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
};

const inputBarStyle: React.CSSProperties = {
    display: "flex",
    gap: "8px",
    padding: "10px 0",
    background: "#fff",
};

const textareaStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 38,
    maxHeight: 120,
    height: 50,
    resize: "none",
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #57B6B2",
    fontSize: 14,
    fontFamily: "inherit",
};

const buttonStyle: React.CSSProperties = {
    minWidth: 88,
    padding: "8px 12px",
    borderRadius: 6,
    border: "none",
    background: "#001C71",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
};

const topBarStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-start",
    padding: "8px 12px",
    background: "transparent",
};

// floating clear button style
const clearButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: 12,
    left: 12,
    width: 36,
    height: 36,
    borderRadius: 18,
    border: "none",
    background: "#e5e7eb",
    color: "#111827",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
    zIndex: 10,
    padding: 0,
};

function makeId() {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function AiAgent({ authToken, identityProvider }: AiAgentProps) {
    const [draft, setDraft] = useState("");
    const [loading, setLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const listRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const STORAGE_KEYS = {
        sessionId: "aiAgent.sessionId",
        messages: "aiAgent.messages",
    };

    // load persisted conversation on mount
    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            const sid = localStorage.getItem(STORAGE_KEYS.sessionId);
            if (sid) setSessionId(sid);

            const raw = localStorage.getItem(STORAGE_KEYS.messages);
            if (raw) {
                const parsed = JSON.parse(raw) as Message[];
                if (Array.isArray(parsed)) {
                    setMessages(parsed);
                }
            }
        } catch {
            // ignore parse/storage errors
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // persist whenever messages or sessionId change
    useEffect(() => {
        if (typeof window === "undefined") return;
        try {
            localStorage.setItem(STORAGE_KEYS.sessionId, sessionId ?? "");
            localStorage.setItem(STORAGE_KEYS.messages, JSON.stringify(messages));
        } catch {
            // ignore quota/errors
        }
    }, [messages, sessionId]);

    // auto-scroll to bottom whenever messages change or loading changes
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        // scroll into view the bottom element for consistent behavior
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages, loading]);

    const appendMessage = (m: Message) => {
        setMessages((prev) => [...prev, m]);
    };

    const clearConversation = () => {
        setMessages([]);
        setSessionId(null);
        if (typeof window !== "undefined") {
            try {
                localStorage.removeItem(STORAGE_KEYS.messages);
                localStorage.removeItem(STORAGE_KEYS.sessionId);
            } catch {
                // ignore
            }
        }
    };

    const contextInfo = async (): Promise<string> => {
        try {
            let infoLines = ["Context: BuilderMetrix add-in for Excel (HomeBuilders)"];

            return await Excel.run(async (context) => {
                context.workbook.load("name");
                await context.sync();
                infoLines.push(`Workbook: ${fileName(context.workbook.name)}`);

                let templateName = "";
                let templateNameRange = context.workbook.names.getItemOrNullObject("template");
                await context.sync();
                if (!templateNameRange.isNullObject) {
                    const range = templateNameRange.getRange();
                    range.load("values");
                    await context.sync();
                    templateName = range.values[0][0] || "";
                }
                if (templateName) {
                    infoLines.push(`Analyzer: ${templateName}`);
                }

                let paramters = "";
                let paramtersRange = context.workbook.names.getItemOrNullObject("parameters");
                await context.sync();
                if (!paramtersRange.isNullObject) {
                    const range = paramtersRange.getRange();
                    range.load("values");
                    await context.sync();
                    paramters = range.values[0][0] || "";
                }
                if (paramters) {
                    infoLines.push(`Parameters: ${paramters}`);
                }

                const sheet = context.workbook.worksheets.getActiveWorksheet();
                sheet.load("name");
                await context.sync();
                infoLines.push(`Worksheet: ${sheet.name}`);

                // Also get selected range values preview, provided as: r1c1, r1c2, ... | ... | rnc1, rnc2, ...
                const usedRange = sheet.context.workbook.getSelectedRange();
                usedRange.load("values");
                await context.sync();
                const usedValues = usedRange.values;
                const previewValues = usedValues.slice(0, 10).map(row => row.slice(0, 10).join(", ")).join(" | ");
                infoLines.push(`Selected values (preview, first max. 10x10): ${previewValues || "N/A"}`);

                return infoLines.join("\n");
            });
        } catch {
            return "";
        }
    };

    const sendPrompt = async (prompt: string) => {
        if (!prompt.trim()) return;
        const userMsg: Message = {
            id: makeId(),
            role: "user",
            text: prompt,
            timestamp: Date.now(),
        };
        appendMessage(userMsg);
        setDraft("");
        setLoading(true);

        const assistantPlaceholder: Message = {
            id: makeId(),
            role: "assistant",
            text: "Thinking...",
            timestamp: Date.now(),
        };
        appendMessage(assistantPlaceholder);

        let info = await contextInfo();
        if (info) {
            prompt += `\n\n${info}`;
        }

        try {
            const res = await api.post("ai-agent",
                { 
                    query: prompt,
                    sessionId,
                },
                authToken,
                identityProvider,
            );

            if (!res.ok) {
                // Read the message out of the error body rather than showing the raw JSON envelope.
                throw await responseError(res, ErrorMessages.AiAgentFailed);
            }

            const data = await res.json();
            
            setSessionId(data.sessionID || sessionId);

            let replyText = data.response;

            // replace the assistant placeholder last message
            setMessages((prev) => {
                const replaced = [...prev];
                // find last assistant placeholder id (from above)
                const lastIdx = replaced.map((m) => m.role).lastIndexOf("assistant");
                if (lastIdx >= 0) {
                    replaced[lastIdx] = {
                        ...replaced[lastIdx],
                        text: replyText,
                        timestamp: Date.now(),
                    };
                } else {
                    replaced.push({
                        id: makeId(),
                        role: "assistant",
                        text: replyText,
                        timestamp: Date.now(),
                    });
                }
                return replaced;
            });
        } catch (err) {
            const msg = "AI service unavailable.";
            setMessages((prev) => [
                ...prev,
                {
                    id: makeId(),
                    role: "assistant",
                    text: `Error: ${msg}`,
                    timestamp: Date.now(),
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (loading) return;
        await sendPrompt(draft);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void handleSubmit();
        }
    };

    return (
        <div style={containerStyle}>
            {/* floating X clear button — uses title for tooltip */}
            <button
                type="button"
                onClick={clearConversation}
                style={{
                    ...clearButtonStyle,
                    display: messages.length === 0 && !loading ? "none" : undefined,
                }}
                title="Clear conversation"
                aria-label="Clear conversation"
                aria-hidden={messages.length === 0 && !loading}
                disabled={messages.length === 0 && !loading}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            <div ref={listRef} style={listStyle} aria-live="polite">
                {messages.length === 0 && (
                    <div style={{ color: "#6D6E71", textAlign: "center", marginTop: 18 }}>
                        Start the chat by typing a prompt below.
                    </div>
                )}
                {messages.map((m) => {
                    const isUser = m.role === "user";
                    const bubbleStyle: React.CSSProperties = {
                        ...bubbleBase,
                        alignSelf: isUser ? "flex-end" : "flex-start",
                        background: isUser ? "#001C71" : "#fff",
                        color: isUser ? "#fff" : "#111827",
                        boxShadow: isUser ? "0 1px 2px rgba(0,0,0,0.08)" : "0 1px 2px rgba(15,23,42,0.04)",
                        marginTop: "6px",
                        marginBottom: "6px",
                    };
                    return (
                        <div key={m.id} style={{ display: "flex", flexDirection: "column" }}>
                            <div style={bubbleStyle}>
                                {m.text}
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            <div style={inputBarStyle}>
                <textarea
                    aria-label="Type your prompt"
                    placeholder={"Ask something...\n(The current context will be automatically included)"}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={textareaStyle}
                    disabled={loading}
                />
                <button
                    type="button"
                    style={{
                        ...buttonStyle,
                        opacity: loading ? 0.7 : 1,
                        pointerEvents: loading ? "none" : "auto",
                    }}
                    onClick={() => void handleSubmit()}
                    aria-disabled={loading}
                >
                    {loading ? "Asking…" : "Ask"}
                </button>
            </div>
        </div>
    );
}